import React, { useState } from 'react';
import { ArrowLeft, Download, CreditCard, Mail, MapPin, FileText, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import billingData from '../../data/billing.json';

const BillingManagement: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedInvoice, setSelectedInvoice] = useState<string | null>(null);

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'paid':
        return t('subscription.billing.statusPaid');
      case 'pending':
        return t('subscription.billing.statusPending');
      case 'overdue':
        return t('subscription.billing.statusOverdue');
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'overdue':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'overdue':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const handleDownloadPdf = (invoiceId: string, pdfUrl: string) => {
    console.log(`Downloading invoice ${invoiceId} from ${pdfUrl}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const totalPaid = billingData.invoices
    .filter(inv => inv.status === 'paid')
    .reduce((sum, inv) => sum + inv.amount, 0);

  const totalPending = billingData.invoices
    .filter(inv => inv.status === 'pending')
    .reduce((sum, inv) => sum + inv.amount, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate('/my-subscription')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">{t('subscription.billing.backToSubscription')}</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('subscription.billing.heading')}</h1>
          <p className="text-gray-600">{t('subscription.billing.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6 border-2 border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-600 uppercase">{t('subscription.billing.totalPaid')}</h3>
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">€{totalPaid.toFixed(2)}</p>
            <p className="text-xs text-gray-500 mt-2">{t('subscription.billing.allTime')}</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border-2 border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-600 uppercase">{t('subscription.billing.pending')}</h3>
              <Clock className="w-5 h-5 text-yellow-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">€{totalPending.toFixed(2)}</p>
            <p className="text-xs text-gray-500 mt-2">{t('subscription.billing.outstandingBalance')}</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border-2 border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-600 uppercase">{t('subscription.billing.totalInvoices')}</h3>
              <FileText className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{billingData.invoices.length}</p>
            <p className="text-xs text-gray-500 mt-2">{t('subscription.billing.generatedInvoices')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">{t('subscription.billing.invoices')}</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">{t('subscription.billing.columnInvoice')}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">{t('subscription.billing.columnDate')}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">{t('subscription.billing.columnAmount')}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">{t('subscription.billing.columnStatus')}</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase">{t('subscription.billing.columnAction')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billingData.invoices.map((invoice) => (
                      <tr
                        key={invoice.id}
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => setSelectedInvoice(invoice.id)}
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-2">
                            <FileText className="w-4 h-4 text-gray-400" />
                            <span className="font-semibold text-gray-900">{invoice.id}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-600">
                          {formatDate(invoice.date)}
                        </td>
                        <td className="py-4 px-4">
                          <span className="font-semibold text-gray-900">
                            €{invoice.amount.toFixed(2)}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(invoice.status)}`}>
                            {getStatusIcon(invoice.status)}
                            <span className="capitalize">{getStatusLabel(invoice.status)}</span>
                          </span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadPdf(invoice.id, invoice.pdfUrl);
                            }}
                            className="inline-flex items-center space-x-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium transition-colors"
                          >
                            <Download className="w-3 h-3" />
                            <span>{t('subscription.billing.pdf')}</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-6 sticky top-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">{t('subscription.billing.billingInfo')}</h2>

              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">{t('subscription.billing.companyName')}</p>
                  <p className="text-sm text-gray-900 font-medium">{billingData.billingInfo.companyName}</p>
                </div>

                <div className="flex items-start space-x-3">
                  <Mail className="w-4 h-4 text-gray-400 mt-1" />
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{t('subscription.billing.email')}</p>
                    <p className="text-sm text-gray-900">{billingData.billingInfo.email}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <MapPin className="w-4 h-4 text-gray-400 mt-1" />
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{t('subscription.billing.address')}</p>
                    <p className="text-sm text-gray-900">{billingData.billingInfo.address}</p>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">{t('subscription.billing.taxId')}</p>
                  <p className="text-sm text-gray-900 font-mono">{billingData.billingInfo.taxId}</p>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-start space-x-3">
                    <CreditCard className="w-4 h-4 text-gray-400 mt-1" />
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-2">{t('subscription.billing.paymentMethod')}</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-900 font-medium capitalize">
                            {t('subscription.billing.cardLast4', { brand: billingData.billingInfo.paymentMethod.brand, last4: billingData.billingInfo.paymentMethod.last4 })}
                          </p>
                          <p className="text-xs text-gray-500">
                            {t('subscription.billing.expires', { month: billingData.billingInfo.paymentMethod.expiryMonth, year: billingData.billingInfo.paymentMethod.expiryYear })}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <button className="w-full mt-4 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold transition-colors">
                  {t('subscription.billing.updateBillingInfo')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {selectedInvoice && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8">
              {(() => {
                const invoice = billingData.invoices.find(inv => inv.id === selectedInvoice);
                if (!invoice) return null;

                return (
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-2xl font-bold text-gray-900">{t('subscription.billing.invoiceDetails')}</h3>
                      <button
                        onClick={() => setSelectedInvoice(null)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    </div>

                    <div className="space-y-4 mb-6">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('subscription.billing.invoiceNumber')}</span>
                        <span className="font-semibold text-gray-900">{invoice.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('subscription.billing.date')}</span>
                        <span className="text-gray-900">{formatDate(invoice.date)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('subscription.billing.dueDate')}</span>
                        <span className="text-gray-900">{formatDate(invoice.dueDate)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('subscription.billing.status')}</span>
                        <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(invoice.status)}`}>
                          {getStatusIcon(invoice.status)}
                          <span className="capitalize">{getStatusLabel(invoice.status)}</span>
                        </span>
                      </div>
                    </div>

                    <div className="border-t border-b border-gray-200 py-4 mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">{t('subscription.billing.items')}</h4>
                      {invoice.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between py-2">
                          <div>
                            <p className="text-gray-900">{item.description}</p>
                            <p className="text-xs text-gray-500">{t('subscription.billing.qty', { quantity: item.quantity, price: item.unitPrice.toFixed(2) })}</p>
                          </div>
                          <span className="font-semibold text-gray-900">€{item.total.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-between items-center mb-6">
                      <span className="text-lg font-semibold text-gray-900">{t('subscription.billing.totalAmount')}</span>
                      <span className="text-2xl font-bold text-gray-900">€{invoice.amount.toFixed(2)}</span>
                    </div>

                    <div className="flex space-x-3">
                      <button
                        onClick={() => setSelectedInvoice(null)}
                        className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold transition-colors"
                      >
                        {t('subscription.billing.close')}
                      </button>
                      <button
                        onClick={() => handleDownloadPdf(invoice.id, invoice.pdfUrl)}
                        className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
                      >
                        <Download className="w-4 h-4" />
                        <span>{t('subscription.billing.downloadPdf')}</span>
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingManagement;
